import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronUp, ChevronDown, Download as DownloadIcon, FileText, MessageCircle, Phone, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import { useSiteContent } from '../context/SiteContentContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tab = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
      active ? 'border-[#0a3d8f] text-[#0a3d8f]' : 'border-transparent text-gray-500 hover:text-gray-800'
    }`}
  >
    {children}
  </button>
);

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [tab, setTab] = useState('description');
  const { config } = useSiteContent();

  useEffect(() => {
    setLoading(true);
    setErr('');
    setGalleryIdx(0);
    window.scrollTo({ top: 0, behavior: 'auto' });
    axios
      .get(`${API}/products/${productId}`)
      .then((r) => setProduct(r.data))
      .catch(() => setErr('Produk tidak ditemukan.'))
      .finally(() => setLoading(false));
    axios.get(`${API}/products/${productId}/related?limit=4`).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [productId]);

  const gallery = useMemo(() => {
    const list = product?.gallery_images?.length ? product.gallery_images : [product?.image].filter(Boolean);
    return list;
  }, [product]);

  const coreFeatures = product?.core_features_id?.length ? product.core_features_id : product?.core_features_en || product?.features || [];
  const description = product?.description_id || product?.description_en || product?.description || '';
  const featureBlocks = product?.feature_blocks_id?.length ? product.feature_blocks_id : product?.feature_blocks_en || [];
  const specs = product?.specifications_id?.length ? product.specifications_id : product?.specifications_en || [];
  const downloads = product?.downloads || [];

  const whatsapp = (config.whatsapp || '').replace(/\D/g, '');
  const whatsappHref = whatsapp
    ? `https://wa.me/${whatsapp}?text=${encodeURIComponent(`Halo, saya tertarik dengan ${product?.name || 'produk JILONG'}. Mohon informasi lebih lanjut.`)}`
    : '#';

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-[60vh] flex items-center justify-center">
          <p className="text-gray-500">Memuat produk...</p>
        </main>
        <Footer />
      </>
    );
  }
  if (err || !product) {
    return (
      <>
        <Header />
        <main className="min-h-[60vh] flex items-center justify-center flex-col gap-3">
          <p className="text-gray-700 font-semibold">{err || 'Produk tidak ditemukan.'}</p>
          <Link to="/produk" className="text-[#0a3d8f] hover:underline text-sm">← Kembali ke Produk</Link>
        </main>
        <Footer />
      </>
    );
  }

  const productJsonLd = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.seo_description || product.tagline || product.description || '',
    image: product.image || product.gallery_images?.[0] || '',
    sku: product.model || product.id,
    brand: { '@type': 'Brand', name: 'JILONG' },
    category: product.category || '',
  } : null;

  return (
    <>
      <SEO
        path={`/produk/detail/${product.id}`}
        pageName={product.name}
        title={product.seo_title || `${product.name} — JILONG Indonesia`}
        description={product.seo_description || product.tagline || product.description}
        ogImage={product.seo_og_image || product.image || product.gallery_images?.[0]}
        jsonLd={productJsonLd ? [productJsonLd] : []}
      />
      <Header />
      <main data-testid="product-detail-page" className="bg-white">
        {/* Breadcrumb */}
        <div className="border-b border-gray-100 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 py-3 text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
            <Link to="/" className="hover:text-[#0a3d8f]">Beranda</Link>
            <ChevronRight size={12} />
            <Link to="/produk" className="hover:text-[#0a3d8f]">Produk</Link>
            <ChevronRight size={12} />
            <Link to={`/produk/${product.category}`} className="hover:text-[#0a3d8f]">{product.category}</Link>
            <ChevronRight size={12} />
            <span className="text-gray-900 font-semibold truncate">{product.name}</span>
          </div>
        </div>

        {/* Product Name */}
        <div className="max-w-7xl mx-auto px-4 pt-8 pb-4">
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900" data-testid="product-name">
            {product.name}
          </h1>
          {product.tagline && <p className="text-gray-500 mt-1">{product.tagline}</p>}
        </div>

        {/* Gallery + Core features */}
        <div className="max-w-7xl mx-auto px-4 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: thumbnails column (wider, with scroll arrows) */}
          <div className="hidden lg:flex lg:col-span-2 flex-col gap-2">
            <button
              onClick={() => setGalleryIdx((i) => Math.max(0, i - 1))}
              className="w-full py-1 border border-gray-200 rounded-md text-gray-400 hover:text-[#0a3d8f] hover:border-[#0a3d8f] disabled:opacity-40"
              disabled={galleryIdx === 0}
            >
              <ChevronUp size={16} className="mx-auto" />
            </button>
            <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-1">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setGalleryIdx(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`w-full aspect-square border-2 rounded-md overflow-hidden bg-white transition shrink-0 ${
                    galleryIdx === i ? 'border-[#0a3d8f] ring-2 ring-[#0a3d8f]/20' : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
            <button
              onClick={() => setGalleryIdx((i) => Math.min(gallery.length - 1, i + 1))}
              className="w-full py-1 border border-gray-200 rounded-md text-gray-400 hover:text-[#0a3d8f] hover:border-[#0a3d8f] disabled:opacity-40"
              disabled={galleryIdx >= gallery.length - 1}
            >
              <ChevronDown size={16} className="mx-auto" />
            </button>
          </div>

          {/* Center: main image */}
          <div className="lg:col-span-6 bg-gradient-to-br from-slate-50 to-blue-50/40 rounded-lg overflow-hidden flex items-center justify-center min-h-[420px] p-6 relative">
            {gallery[galleryIdx] ? (
              <img
                src={gallery[galleryIdx]}
                alt={product.name}
                className="max-w-full max-h-[480px] object-contain"
                data-testid="gallery-main"
              />
            ) : (
              <div className="text-gray-400">Tidak ada gambar</div>
            )}
            {gallery.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs bg-white/80 backdrop-blur px-2 py-1 rounded-full text-gray-600">
                {galleryIdx + 1} / {gallery.length}
              </div>
            )}
          </div>

          {/* Right: Downloads tabs + Core features + Recommended */}
          <div className="lg:col-span-4">
            {downloads.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-3 border-b border-gray-200 mb-4 text-sm">
                {downloads.slice(0, 4).map((d, i) => (
                  <a
                    key={i}
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`download-${i}`}
                    className="inline-flex items-center gap-1.5 text-gray-700 hover:text-[#0a3d8f]"
                  >
                    <FileText size={14} /> {d.name}
                  </a>
                ))}
              </div>
            )}

            {coreFeatures.length > 0 && (
              <div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-3">Core Features</h3>
                <ul className="space-y-2">
                  {coreFeatures.map((f, i) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0a8f3d] mt-2 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {related.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-wider mb-3">Recommended Products</h3>
                <div className="grid grid-cols-4 gap-2">
                  {related.slice(0, 4).map((r) => (
                    <Link
                      key={r.id}
                      to={`/produk/detail/${r.id}`}
                      data-testid={`recommended-${r.id}`}
                      className="group block bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-md aspect-square p-2 border border-gray-100 hover:border-[#0a3d8f] transition"
                      title={r.name}
                    >
                      <img src={r.image} alt={r.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CTA buttons */}
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={whatsappHref}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-quote"
                className="inline-flex items-center gap-2 bg-[#0a8f3d] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#08702f]"
              >
                <MessageCircle size={16} /> Request Quote
              </a>
              <Link
                to="/kontak"
                data-testid="cta-consult"
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-gray-50"
              >
                <Phone size={16} /> Online Consultation
              </Link>
              {product.link && (
                <a
                  href={product.link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-gray-500 text-xs font-medium hover:text-[#0a3d8f] px-2"
                >
                  Halaman resmi <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-t border-b border-gray-200 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 flex overflow-x-auto">
            <Tab active={tab === 'description'} onClick={() => setTab('description')} testId="tab-description">Description</Tab>
            {specs.length > 0 && <Tab active={tab === 'spec'} onClick={() => setTab('spec')} testId="tab-spec">Specification</Tab>}
            {downloads.length > 0 && <Tab active={tab === 'download'} onClick={() => setTab('download')} testId="tab-download">Download</Tab>}
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {tab === 'description' && (
            <div>
              {description && (
                <div className="text-gray-700 leading-relaxed text-[15px] max-w-4xl">
                  {description}
                </div>
              )}
              {featureBlocks.length > 0 && (
                <div className="mt-10 space-y-10">
                  <h2 className="text-2xl font-extrabold text-gray-900 border-b-2 border-[#0a8f3d] pb-2 inline-block">Features</h2>
                  {featureBlocks.map((b, i) => {
                    const rev = i % 2 === 1;
                    return (
                      <div
                        key={i}
                        data-testid={`feature-block-${i}`}
                        className={`grid grid-cols-1 md:grid-cols-2 gap-6 items-center ${rev ? 'md:[&>div:first-child]:order-2' : ''}`}
                      >
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden aspect-[4/3] flex items-center justify-center">
                          {b.image ? (
                            <img src={b.image} alt={b.title} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-gray-300 text-sm">Tidak ada gambar</span>
                          )}
                        </div>
                        <div>
                          {b.title && <h3 className="text-lg md:text-xl font-extrabold text-gray-900 mb-2">{b.title}</h3>}
                          {b.description && <p className="text-gray-600 text-sm md:text-[15px] leading-relaxed">{b.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === 'spec' && (
            <div className="max-w-4xl">
              <table className="w-full text-sm border border-gray-200">
                <tbody>
                  {specs.map((s, i) => (
                    <tr key={i} data-testid={`spec-row-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-semibold text-gray-800 border-b border-gray-100 w-1/3">{s.key}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{s.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'download' && (
            <div className="max-w-3xl space-y-3">
              {downloads.map((d, i) => (
                <a
                  key={i}
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`download-item-${i}`}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:border-[#0a3d8f] hover:shadow-sm transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#0a3d8f]/10 text-[#0a3d8f] flex items-center justify-center">
                      <FileText size={18} />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{d.name}</div>
                      <div className="text-xs text-gray-500 truncate max-w-md">{d.url}</div>
                    </div>
                  </div>
                  <DownloadIcon size={18} className="text-gray-400" />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <section className="bg-gray-50 border-t border-gray-100 py-12" data-testid="related-products">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-900">Produk Sejenis</h2>
                  <p className="text-sm text-gray-500 mt-1">Rekomendasi produk pada kategori yang sama</p>
                </div>
                <Link to="/produk" className="text-sm font-semibold text-[#0a3d8f] hover:underline hidden sm:inline-flex items-center gap-1">
                  Lihat Semua <ChevronRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/produk/detail/${r.id}`}
                    data-testid={`related-item-${r.id}`}
                    className="group bg-white rounded-lg border border-gray-200 hover:border-[#0a3d8f] hover:shadow-md transition overflow-hidden"
                  >
                    <div className="h-40 bg-gradient-to-br from-gray-50 to-white p-4 flex items-center justify-center relative">
                      <img
                        src={r.image}
                        alt={r.name}
                        className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                      />
                      {r.model && (
                        <div className="absolute top-2 left-2 bg-[#0a3d8f] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wider">
                          {r.model}
                        </div>
                      )}
                    </div>
                    <div className="p-3 border-t border-gray-100">
                      <h4 className="text-sm font-bold text-gray-900 line-clamp-2 min-h-[2.5rem] group-hover:text-[#0a3d8f] transition-colors">
                        {r.name}
                      </h4>
                      <span className="text-xs text-[#0a3d8f] font-semibold mt-2 inline-flex items-center gap-1">
                        Lihat Detail <ChevronRight size={12} />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

export default ProductDetailPage;
